import type { ApiSchema } from "../../../api/apiSchema";
import { requestJson } from "../../../api/http/httpClient";
import { JsonContractError, readArray, readObject, readOneOf, readString, type JsonObject } from "../../../api/http/jsonDecoding";

export const SEARCH_KINDS = ["step", "workflow", "artifact", "skill", "label_list", "prompt", "tool"] as const satisfies readonly ApiSchema<"ProcessSearchHit">["kind"][];
export type SearchKind = typeof SEARCH_KINDS[number];

/** One exact version on the project's latest main, never a proposed change. */
export interface ProcessSearchItem extends Pick<ApiSchema<"ProcessSearchHit">, "name" | "description" | "version" | "reference" | "score"> {
  readonly kind: SearchKind;
  readonly itemId: string;
  readonly details: JsonObject;
}

export function searchProcessItems(projectId: string, query: string, kinds: readonly SearchKind[], signal?: AbortSignal): Promise<readonly ProcessSearchItem[]> {
  const parameters = new URLSearchParams({ query, limit: "5" });
  for (const kind of kinds) parameters.append("kinds", kind);
  return requestJson({
    method: "GET",
    path: `/projects/${encodeURIComponent(projectId)}/content/process-items/search?${parameters.toString()}`,
    signal,
    decodeResponse: (body) => readArray(readObject(body, "process search")["items"], "search items", (value) => {
      const item = readObject(value, "search item");
      const score = item["score"];
      if (typeof score !== "number" || !Number.isFinite(score)) throw new JsonContractError("Expected a finite search score.");
      return {
        kind: readOneOf(item, "kind", SEARCH_KINDS), itemId: readString(item, "item_id"),
        name: readString(item, "name"), description: readString(item, "description"),
        version: readString(item, "version"), reference: readString(item, "reference"),
        details: readObject(item["details"], "search details"), score,
      };
    }),
  });
}
